'use client';

import { useTranslations } from 'next-intl';
import React, { useEffect, useMemo } from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconChecked16 } from '@cineamo/ui/src/icons/ic-checked';
import { IconClose16 } from '@cineamo/ui/src/icons/ic-close';
import { seatColors } from '@cineamo/ui/src/seat-map/exampleData';
import type { Seat, SeatCategory } from '@cineamo/ui/src/seat-map/typedefs';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';
import { useCartProvider } from '@cineamo/composables/src/providers/cartProvider';

import { defaultPricesByCategory, pickSeatUpsell, type SeatUpsell as SeatUpsellOffer } from './seat-showcase';
import { SeatCategoryVisual } from './SeatCategoryShowcaseCard';

/**
 * The cart's hint that a better seat category has room for the whole party (CIN-15400). `seats` carry their lock
 * and selection state. Tapping "Wechseln" hands the group to `onSwap`.
 */
const SeatUpsell: React.FC<{
    seats: Seat[];
    /** The room's categories, in the order the legend and the map color them by. */
    seatCategories: SeatCategory[];
    /** True once per showing and category: the page owns the record, as this unmounts between steps. */
    shouldTrackShown: (categoryId: SeatCategory['id']) => boolean;
    currency: string;
    isBusy?: boolean;
    /** The guest said no (x or undo): nothing is offered for the rest of the visit. */
    isDeclined?: boolean;
    onSwap: (offer: SeatUpsellOffer) => void;
    /** Opens the target category's showcase modal from its photo and title. */
    onShowCategory?: (category: SeatCategory) => void;
    onDismiss: () => void;
    className?: string;
}> = ({
    seats,
    seatCategories,
    shouldTrackShown,
    currency,
    isBusy,
    isDeclined,
    onSwap,
    onShowCategory,
    onDismiss,
    className
}) => {
    const t = useTranslations('tickets');
    const formatter = useFormat();
    const { track } = useAnalytics();
    const { selectedSeats, priceOptions } = useCartProvider();
    const upsell = useMemo(() => {
        const prices = defaultPricesByCategory(priceOptions);
        return pickSeatUpsell(seats, {
            isDeclined,
            defaultPrice: (categoryId) => prices.get(categoryId),
            selected: selectedSeats,
            defaultPriceOptionId: (categoryId) =>
                priceOptions.find(
                    (option) => option._embedded?.seatCategory?.id === categoryId && option.isShowingDefault
                )?.id
        });
    }, [seats, selectedSeats, priceOptions, isDeclined]);

    const categoryId = upsell?.category.id ?? null;
    const priceDifference = upsell?.priceDifference;
    useEffect(() => {
        if (categoryId === null || !shouldTrackShown(categoryId)) return;
        track('Seat Upsell Shown', { categoryId, priceDifference });
    }, [categoryId, priceDifference, shouldTrackShown, track]);

    if (!upsell) return null;
    const { category } = upsell;
    const color =
        category.color ??
        seatColors[
            Math.max(
                0,
                seatCategories.findIndex((c) => c.id === category.id)
            ) % seatColors.length
        ];
    const eventProperties = { categoryId: category.id, priceDifference: upsell.priceDifference };
    const open = onShowCategory && (() => onShowCategory(category));

    return (
        <section
            aria-label={category.name}
            className={cn(
                // Phones: photo, offer and buttons in one row, the benefit full width below; desktop puts the buttons beside the benefit.
                'grid grid-cols-[auto_1fr_auto] items-center gap-x-8 gap-y-4 rounded-4 border border-[var(--on-background-color)] p-8 text-[var(--on-background-color)] lg:gap-x-12 lg:gap-y-8 lg:p-12',
                className
            )}>
            <button
                type="button"
                onClick={open}
                tabIndex={open ? undefined : -1}
                aria-label={t('seat-category-info', { name: category.name })}
                className="rounded-4 outline-none focus-visible:ring-2 focus-visible:ring-[var(--on-background-color)]">
                <SeatCategoryVisual
                    category={category}
                    color={color}
                    className={
                        category.imageUrl ? 'size-[40px] shrink-0 lg:h-[48px] lg:w-[64px]' : 'size-[24px] shrink-0'
                    }
                />
            </button>
            <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
                <p className="text-body-bold-12 lg:text-body-bold-14">
                    <button
                        type="button"
                        onClick={open}
                        className="text-left outline-none focus-visible:ring-2 focus-visible:ring-[var(--on-background-color)]">
                        {t('seat-upsell-title', { name: category.name })}
                    </button>
                </p>
                <p className="text-body-12">
                    {t('seat-upsell-price', {
                        count: upsell.seats.length,
                        price: formatter.number.formatCurrency(upsell.priceDifference / 100, currency),
                        total: formatter.number.formatCurrency(
                            (upsell.priceDifference * upsell.seats.length) / 100,
                            currency
                        )
                    })}
                    {upsell.seats.length > 1 && (
                        // The legend and the card name the per-seat price; on phones the total is what counts.
                        <span className="hidden lg:inline">
                            {' '}
                            (
                            {t('seat-category-surcharge', {
                                price: formatter.number.formatCurrency(upsell.priceDifference / 100, currency)
                            })}
                            )
                        </span>
                    )}
                </p>
            </div>
            {category.benefits?.[0] && (
                <p className="col-span-3 flex items-start gap-6 text-body-12 lg:col-span-2 lg:row-start-2">
                    <IconChecked16
                        className="mt-2 shrink-0 text-[var(--primary-color)]"
                        fill="currentColor"
                    />
                    <span>{category.benefits[0]}</span>
                </p>
            )}
            <div className="col-start-3 row-start-1 flex shrink-0 items-center justify-end gap-4 lg:row-start-2">
                <button
                    type="button"
                    aria-label={t('seat-upsell-dismiss')}
                    onClick={() => {
                        track('Seat Upsell Dismissed', eventProperties);
                        onDismiss();
                    }}
                    className="flex size-[40px] items-center justify-center text-body-bold-14 outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)] lg:size-40">
                    <IconClose16 fill="currentColor" />
                </button>
                <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => {
                        track('Seat Upsell Accepted', { ...eventProperties, source: 'card' });
                        onSwap(upsell);
                    }}
                    className="min-h-[44px] rounded-4 border-2 border-[var(--primary-color)] px-12 text-body-bold-14 text-[var(--primary-color)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)] focus-visible:ring-offset-2 disabled:opacity-50 lg:min-h-40 lg:px-16">
                    {t('seat-upsell-accept')}
                </button>
            </div>
        </section>
    );
};

/** What replaces the hint after a swap: says what changed and offers the way back while nothing else has. */
export const SeatUpsellNotice: React.FC<{
    name: string;
    /** What the swap added to the order, formatted. */
    total: string;
    /** The swap put a party together that was split before: the strongest argument, said in the line itself. */
    isTogether?: boolean;
    isBusy?: boolean;
    onUndo: () => void;
    /** The next category up that fits the party: a quiet link that opens its showcase, where the swap happens. */
    next?: { name: string; price: string; onOpen: () => void };
    className?: string;
}> = ({ name, total, isTogether, isBusy, onUndo, next, className }) => {
    const t = useTranslations('tickets');
    return (
        <p
            role="status"
            className={cn(
                'flex items-center justify-between gap-12 rounded-4 border border-[var(--on-background-color)] p-12 text-body-14 text-[var(--on-background-color)]',
                className
            )}>
            <span className="min-w-0">
                {t(isTogether ? 'seat-upsell-swapped-together' : 'seat-upsell-swapped', { name, total })}
                {next && (
                    <>
                        {' '}
                        <button
                            type="button"
                            disabled={isBusy}
                            onClick={next.onOpen}
                            className="text-left underline outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)] disabled:opacity-50">
                            {t('seat-upsell-next', { name: next.name, price: next.price })} {'\u203A'}
                        </button>
                    </>
                )}
            </span>
            <button
                type="button"
                disabled={isBusy}
                onClick={onUndo}
                className="min-h-40 shrink-0 px-4 text-body-bold-14 underline outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)] disabled:opacity-50">
                {t('seat-upsell-undo')}
            </button>
        </p>
    );
};

export default SeatUpsell;
